import { useState, useEffect, useRef } from 'react';

/**
 * Animates HTML content with a typing effect similar to GPT/Gemini.
 *
 * Instead of slicing the raw HTML string (which would partially reveal tags),
 * this hook parses the HTML into a DOM tree once, collects all text nodes in
 * order, and progressively reveals them character-by-character while always
 * keeping the full tag structure intact.
 *
 * @param html          The full HTML string to reveal.
 * @param enabled       When false the full string is returned immediately.
 * @param charsPerTick  Characters revealed per tick (default 3).
 * @param intervalMs    Milliseconds between ticks (default 30 ≈ ~100 chars/sec).
 * @returns             { displayedHtml, isDone }
 */
export function useHtmlTypingEffect(
  html: string,
  enabled: boolean,
  charsPerTick = 3,
  intervalMs = 30,
): { displayedHtml: string; isDone: boolean } {
  // charIndex = how many total text characters have been revealed so far.
  const [charIndex, setCharIndex] = useState(0);
  const prevHtmlRef = useRef('');
  const prevEnabledRef = useRef(enabled);

  // Total number of text characters in the full HTML (computed once per html change).
  const totalCharsRef = useRef(0);

  // Reset when html or enabled changes.
  useEffect(() => {
    const contentChanged = prevHtmlRef.current !== html;
    const enabledChanged = prevEnabledRef.current !== enabled;

    prevHtmlRef.current = html;
    prevEnabledRef.current = enabled;

    if (!enabled) {
      totalCharsRef.current = countTextChars(html);
      setCharIndex(totalCharsRef.current);
      return;
    }

    if (contentChanged || enabledChanged) {
      totalCharsRef.current = countTextChars(html);
      setCharIndex(0);
    }
  }, [html, enabled]);

  // Advance the char index on each tick.
  useEffect(() => {
    const total = totalCharsRef.current;
    if (!enabled || charIndex >= total) return;

    const id = setInterval(() => {
      setCharIndex((prev) => Math.min(prev + charsPerTick, total));
    }, intervalMs);

    return () => clearInterval(id);
  }, [html, charIndex, enabled, charsPerTick, intervalMs]);

  const total = totalCharsRef.current;

  if (!enabled || charIndex >= total) {
    return { displayedHtml: html, isDone: true };
  }

  const displayedHtml = buildPartialHtml(html, charIndex);
  return { displayedHtml, isDone: false };
}

/** Count total visible text characters in an HTML string. */
function countTextChars(html: string): number {
  if (!html) return 0;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.length ?? 0;
}

/**
 * Rebuild the HTML but with text nodes truncated so that only `targetChars`
 * visible characters are shown. All element structure (tags, attributes) is
 * always present so the DOM is never malformed.
 */
function buildPartialHtml(html: string, targetChars: number): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let remaining = targetChars;

  function walk(node: Node): void {
    if (remaining <= 0) {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = '';
      } else {
        // Clear all children of element nodes beyond the limit
        while (node.firstChild) node.removeChild(node.firstChild);
      }
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (text.length <= remaining) {
        remaining -= text.length;
      } else {
        node.textContent = text.slice(0, remaining);
        remaining = 0;
      }
    } else {
      // Clone children list before walking (walking mutates)
      const children = Array.from(node.childNodes);
      for (const child of children) {
        walk(child);
      }
    }
  }

  walk(doc.body);
  return doc.body.innerHTML;
}
